// useChat — React hook that owns a single conversation's state and wraps
// streamChat() so screens just bind sendMessage/abort and render `messages`.

import { useCallback, useRef, useState } from 'react';
import { streamChat } from './gemini.js';

function uid() {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
}

export function useChat({ model, system, temperature, maxOutputTokens, onComplete } = {}) {
  const [messages, setMessages] = useState([]); // [{ id, role, content, pending? }]
  const [status, setStatus] = useState('idle'); // idle | streaming | error
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text ?? '').trim();
    if (!trimmed || status === 'streaming') return;

    const userMsg = { id: uid(), role: 'user', content: trimmed };
    const aiMsg   = { id: uid(), role: 'model', content: '', pending: true };
    setMessages((m) => [...m, userMsg, aiMsg]);
    setStatus('streaming');
    setError(null);

    const ctl = new AbortController();
    abortRef.current = ctl;

    try {
      const stream = streamChat({
        // Send the user-visible history including the new user message but
        // excluding the placeholder AI bubble — the model would otherwise see
        // an empty model turn at the tail.
        messages: [
          ...messages.filter((m) => !m.pending),
          { role: 'user', content: trimmed },
        ].map(({ role, content }) => ({ role, content })),
        model, system, temperature, maxOutputTokens,
        signal: ctl.signal,
      });

      let finalContent = '';
      for await (const chunk of stream) {
        finalContent += chunk;
        setMessages((all) => all.map((m) =>
          m.id === aiMsg.id ? { ...m, content: m.content + chunk } : m,
        ));
      }
      setMessages((all) => all.map((m) =>
        m.id === aiMsg.id ? { ...m, pending: false } : m,
      ));
      setStatus('idle');

      try {
        await onCompleteRef.current?.({
          user:  { role: 'user',  content: trimmed },
          model: { role: 'model', content: finalContent, modelId: model ?? null },
          aborted: false,
        });
      } catch { /* persistence errors must not break the chat UI */ }
    } catch (e) {
      if (e?.name === 'AbortError') {
        // User-initiated stop — leave the partial reply in place, mark done.
        let partial = '';
        setMessages((all) => {
          const updated = all.map((m) => {
            if (m.id === aiMsg.id) { partial = m.content; return { ...m, pending: false }; }
            return m;
          });
          return updated;
        });
        setStatus('idle');
        try {
          await onCompleteRef.current?.({
            user:  { role: 'user',  content: trimmed },
            model: { role: 'model', content: partial, modelId: model ?? null },
            aborted: true,
          });
        } catch {}
        return;
      }
      setError(e);
      setStatus('error');
      setMessages((all) => all.map((m) =>
        m.id === aiMsg.id
          ? { ...m, pending: false, content: m.content || `_${e.message ?? 'error'}_`, error: true }
          : m,
      ));
    } finally {
      abortRef.current = null;
    }
  }, [messages, model, system, temperature, maxOutputTokens, status]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setError(null);
    setStatus('idle');
  }, []);

  // setMessages is exposed for hydration (loading a saved conversation).
  return { messages, status, error, sendMessage, stop, reset, setMessages };
}
